import {
  Button,
  Divider,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import * as React from "react";
import { fetchData } from "../../../../common/services/ApiClient";
import {
  fileNameFormat,
  sortObjValue,
} from "../../../../common/services/Utilities";
import { ApiBaseUrl } from "../../../../config";
import BranchOfficeDeposits from "../../../../containers/BranchOfficeDeposits";
import ResultPagination from "../../../../containers/ResultPagination";
import { KeyValue, Query, TableHeader } from "../../../../interfaces";
import {
  additionalHeaderColor,
  primaryHeaderColor,
  secondaryHeaderColor,
  textHeaderColor,
} from "../../../../refdata/constants";
import ApiDataDownload from "../../../ApiDataDownload";
import LoadingIndicator from "../../../LoadingIndicator";
import ModalDialog from "../../../ModalDialog";
import MultiHeaderTable from "../../../MultiHeaderTable/MultiHeaderTable";

const PREFIX = "GrowthRateMSATable";

const classes = {
  buttonLink: `${PREFIX}-buttonLink`,
  heading: `${PREFIX}-heading`,
  firstCellDarken: `${PREFIX}-firstCellDarken`,
  firstCellDarkest: `${PREFIX}-firstCellDarkest`,
  groupHover: `${PREFIX}-groupHover`,
  group: `${PREFIX}-group`,
  endDateColor: `${PREFIX}-endDateColor`,
  endDateColorTwo: `${PREFIX}-endDateColorTwo`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.buttonLink}`]: {
    "fontWeight": "bold",
    "color": "#0000FF",
    "padding": 0,
    "marginTop": 0,
    "fontSize": "1rem",
    "&.MuiButton-root:hover, &.MuiButton-root:focus-visible": {
      backgroundColor: "#fff",
      textDecoration: "underline",
    },
  },

  [`& .${classes.heading}`]: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 18,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },

  [`& .${classes.firstCellDarken}`]: {
    minWidth: 60,
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },

  [`& .${classes.firstCellDarkest}`]: {
    minWidth: 60,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
  },

  [`& .${classes.groupHover}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: theme.palette.secondary.main,
      cursor: "pointer",
    },
  },

  [`& .${classes.group}`]: {},

  [`& .${classes.endDateColor}`]: {
    backgroundColor: "#f7f7f7",
  },

  [`& .${classes.endDateColorTwo}`]: {
    backgroundColor: "#ededed",
  },
}));

interface Props {
  appState: Query;
  searchPills: React.ReactElement;
}

export default function GrowthRateMSATables({ appState, searchPills }: Props) {
  const [bankName, setBankName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [totals, setTotals] = React.useState<KeyValue>({});
  const startDate = appState.filters.startDate;
  const endDate = appState.filters.endDate;
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [recordCount, setRecordCount] = React.useState(0);
  const state = React.useMemo(() => {
    return {
      filters: appState.filters,
      search: appState.search,
    };
  }, [appState.filters, appState.search]);
  const [previousState, setPreviousState] = React.useState({});
  const [growthRateTableData, setGrowthRateTableData] = React.useState<
    KeyValue[]
  >([{}]);
  const [open, setOpen] = React.useState({ msaName: "", year: 0 });
  const branchOfficeModal = (
    <ModalDialog
      title={bankName}
      display={open.msaName !== ""}
      setDisplay={() => setOpen({ msaName: "", year: 0 })}
      maxWidth="lg"
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          filters: {
            ...appState.filters,
            msa: open.msaName,
            lastYear: open.year,
          },
        }}
      />
    </ModalDialog>
  );
  const nameOrNameHCR = appState.filters.cert ? "NAMEFULL" : "NAMEHCR";
  const certOrRSSDHCR = appState.filters.cert
    ? `CERT:${appState.filters.cert}`
    : `RSSDHCR:${appState.filters.bhcRSSD}`;
  const headerStyle = (
    bgColor: string,
    align: string,
    textIndent?: string,
    width?: number,
  ): KeyValue => {
    return {
      textAlign: align,
      backgroundColor: bgColor,
      color: textHeaderColor,
      textIndent: textIndent,
      fontWeight: "bold",
      width: width,
    };
  };

  const tableHeaders: TableHeader[][] = [
    [
      {
        title: " ",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 3,
      },
      {
        title: `${startDate}`,
        styles: headerStyle(secondaryHeaderColor, ""),
        colSpan: 2,
      },
      {
        title: `${endDate}`,
        styles: headerStyle(additionalHeaderColor, ""),
        colSpan: 2,
      },
      {
        title: "Change",
        styles: headerStyle(secondaryHeaderColor, ""),
        colSpan: 2,
      },
      {
        title: " ",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 2,
      },
    ],
    (() => {
      const headerValues =
        appState.ui.institutionType === "bhcs"
          ? ["Office Count", "Total Combined Deposits* (Thousands USD)"]
          : ["Office Count", "Total Deposits (Thousands USD)"];

      const msaHeader: TableHeader[] = [
        {
          title: "MSA Name",
          styles: headerStyle(primaryHeaderColor, "left", "5px", 200),
          colSpan: 1,
          apiField: "",
        },
        {
          title: "",
          styles: headerStyle(primaryHeaderColor, "", "", 100),
          colSpan: 1,
          apiField: "",
        },
        {
          title: "MSA Code",
          styles: headerStyle(primaryHeaderColor, "", undefined, 50),
          colSpan: 1,
          apiField: "",
        },
      ];
      const startDateHeader: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: headerStyle(
              secondaryHeaderColor,
              "",
              undefined,
              header === "Office Count" ? 40 : 160,
            ),
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const endDateHeader: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: headerStyle(
              additionalHeaderColor,
              "",
              undefined,
              header === "Office Count" ? 40 : 160,
            ),
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const changeHeader: TableHeader[] = headerValues.map((header: string) => {
        return {
          title: header,
          styles: headerStyle(
            secondaryHeaderColor,
            "",
            undefined,
            header === "Office Count" ? 40 : 160,
          ),
          colSpan: 1,
          apiField: "",
        };
      });

      const growthRateHeader: TableHeader[] = [
        {
          title: "Deposit Growth Rate (%)",
          styles: headerStyle(primaryHeaderColor, "", undefined, 170),
          colSpan: 1,

          apiField: "",
        },
      ];

      return [
        ...msaHeader,
        ...startDateHeader,
        ...endDateHeader,
        ...changeHeader,
        ...growthRateHeader,
      ];
    })(),
  ];

  React.useEffect(() => {
    if (
      certOrRSSDHCR &&
      startDate &&
      endDate &&
      (JSON.stringify(state) !== JSON.stringify(previousState) || initialLoad)
    ) {
      setLoading(true);
      let isMounted = true;
      setPreviousState(state);
      initialLoad && setInitialLoad(false);
      (async () => {
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/sod?filters=${certOrRSSDHCR}&limit=1&sort_by=YEAR&sort_order=DESC`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.startDate
            } AND ${certOrRSSDHCR} AND MSABR:>0 AND !MSANAMB:""&sort_by=MSANAMB&sort_order=ASC&agg_limit=10000&agg_by=MSANAMB&agg_sum_fields=DEPSUMBR`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.endDate
            } AND ${certOrRSSDHCR} AND MSABR:>0 AND !MSANAMB:""&sort_by=MSANAMB&sort_order=ASC&agg_limit=10000&agg_by=MSANAMB&agg_sum_fields=DEPSUMBR`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:(${
              appState.filters.startDate
            } OR ${
              appState.filters.endDate
            }) AND ${certOrRSSDHCR} AND MSABR:>0 AND !MSANAMB:""&fields=MSABR,MSANAMB&limit=10000&get_unique=MSABR`,
          ),
        ])
          .then((response) => {
            if (isMounted) {
              setBankName(
                response[0].data[0] ? response[0].data[0][nameOrNameHCR] : "",
              );

              const normalRate =
                (response[2]?.totals?.sum_DEPSUMBR
                  ? response[2]?.totals?.sum_DEPSUMBR -
                    (response[1]?.totals?.sum_DEPSUMBR || 0)
                  : 0 - (response[1]?.totals?.sum_DEPSUMBR || 0)) /
                response[1]?.totals?.sum_DEPSUMBR;

              const altRate = !response[1]?.totals?.sum_DEPSUMBR
                ? 1
                : normalRate;

              const totals = {
                startDate: {
                  count: response[1]?.totals?.count || 0,
                  sum_DEPSUMBR: response[1]?.totals?.sum_DEPSUMBR || 0,
                },
                endDate: {
                  count: response[2]?.totals?.count || 0,
                  sum_DEPSUMBR: response[2]?.totals?.sum_DEPSUMBR || 0,
                },
                change: {
                  count:
                    (response[2]?.totals?.count || 0) -
                    (response[1]?.totals?.count || 0),
                  sum_DEPSUMBR:
                    (response[2]?.totals?.sum_DEPSUMBR || 0) -
                    (response[1]?.totals?.sum_DEPSUMBR || 0),
                },
                rate:
                  response[1]?.totals?.sum_DEPSUMBR +
                    response[2]?.totals?.sum_DEPSUMBR ===
                  0
                    ? 0
                    : altRate,
              };
              setTotals(totals);

              const allMSAs = Array.from(
                new Set(
                  [...response[1].data, ...response[2].data].map(
                    (res: KeyValue) => res.MSANAMB,
                  ),
                ),
              );

              const tableData: KeyValue[] = allMSAs.map((msa: string) => {
                const matchingStart = response[1].data.find(
                  (res: KeyValue) => res.MSANAMB === msa,
                ) || {
                  MSANAMB: msa,
                  MSABR: [undefined],
                  count: 0,
                  sum_DEPSUMBR: 0,
                };
                const matchingEnd = response[2].data.find(
                  (res: KeyValue) => res.MSANAMB === msa,
                ) || { MSANAMB: msa, count: 0, sum_DEPSUMBR: 0 };

                const MSABR = response[3].data.find(
                  (data: KeyValue) => data.MSANAMB === msa,
                )?.MSABR;

                const start = {
                  count: matchingStart.count || 0,
                  sum_DEPSUMBR: matchingStart.sum_DEPSUMBR || 0,
                };
                const end = {
                  count: matchingEnd.count || 0,
                  sum_DEPSUMBR: matchingEnd.sum_DEPSUMBR || 0,
                };

                const normalRate =
                  (end.sum_DEPSUMBR - start.sum_DEPSUMBR) / start.sum_DEPSUMBR;
                const altRate = start.sum_DEPSUMBR === 0 ? 1 : normalRate;
                const change = {
                  count: end.count - start.count,
                  sum_DEPSUMBR: end.sum_DEPSUMBR - start.sum_DEPSUMBR,
                  rate:
                    start.sum_DEPSUMBR + end.sum_DEPSUMBR === 0 ? 0 : altRate,
                };
                return {
                  MSANAMB: msa,
                  MSABR: MSABR,
                  [startDate]: start,
                  [endDate]: end,
                  change: change,
                };
              });
              setRecordCount(tableData.length);
              setGrowthRateTableData(sortObjValue(tableData, "MSANAMB"));
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [
    appState.filters.endDate,
    appState.filters.startDate,
    certOrRSSDHCR,
    endDate,
    initialLoad,
    nameOrNameHCR,
    previousState,
    startDate,
    state,
  ]);

  const paginatedData =
    growthRateTableData &&
    Object.keys(growthRateTableData).length > 0 &&
    growthRateTableData?.slice(
      (appState.pagination.pageNumber - 1) * appState.pagination.resultLimit,
      appState.pagination.resultLimit * appState.pagination.pageNumber,
    );

  const [groupHover, setGroupHover] = React.useState("");

  const tableCellProps = (
    msaName: string,
    year: number,
    int: number,
    value: string,
    isEndDate?: boolean,
  ) => {
    const onClickFn = (e: any) => {
      if ((e.key && e.key !== "Enter") || value === "0") {
        return;
      }
      if (
        (appState.filters.bhcRSSD || appState.filters.cert) &&
        appState.filters.startDate &&
        appState.filters.endDate
      ) {
        setOpen({ msaName, year });
      }
    };
    return {
      onMouseEnter: () => setGroupHover(`${msaName}_${year}`),
      onMouseLeave: () => setGroupHover(""),
      onFocus: () => setGroupHover(`${msaName}_${year}`),
      onBlur: () => setGroupHover(""),
      onClick: onClickFn,
      onKeyDown: onClickFn,
      tabIndex: 0,
      className: clsx(
        !isEndDate
          ? int % 2 !== 0
            ? classes.firstCellDarken
            : classes.firstCellDarkest
          : int % 2 !== 0
            ? classes.endDateColor
            : classes.endDateColorTwo,
        groupHover === `${msaName}_${year}` && value !== "0"
          ? classes.groupHover
          : classes.group,
      ),
    };
  };

  const downloadHeaders = React.useMemo(
    () => [
      `${appState.filters.bhcRSSD ? "Bank Holding Company Name" : "Bank Name"}`,
      "MSA Name",
      "MSA Code",
      `${appState.filters.cert ? "Cert" : "RSSDHCR"}`,
      `${startDate}: Office Count`,
      `${startDate}: ${
        appState.ui.institutionType === "bhcs"
          ? "Total Combined Deposits"
          : "Total Deposits"
      } (Thousands USD)`,
      `${endDate}: Office Count`,
      `${endDate}: ${
        appState.ui.institutionType === "bhcs"
          ? "Total Combined Deposits"
          : "Total Deposits"
      } (Thousands USD)`,
      "Change: Office Count",
      `Change: ${
        appState.ui.institutionType === "bhcs"
          ? "Total Combined Deposits"
          : "Total Deposits"
      } (Thousands USD)`,
      "Deposit Growth Rate (%)",
    ],
    [
      appState.filters.cert,
      appState.ui.institutionType,
      appState.filters.bhcRSSD,
      endDate,
      startDate,
    ],
  );

  const [downloadData, setDownloadData] = React.useState<string[][]>([
    downloadHeaders,
  ]);
  const [prevData, setPrevData] = React.useState<KeyValue[]>([{}]);

  React.useEffect(() => {
    if (JSON.stringify(prevData) !== JSON.stringify(growthRateTableData)) {
      const newDataArray = growthRateTableData.map((data) => {
        return [
          bankName,
          data.MSANAMB,
          data.MSABR,
          appState.filters.cert
            ? appState.filters.cert
            : appState.filters.bhcRSSD,
          data[startDate]?.count?.toLocaleString(),
          data[startDate]?.sum_DEPSUMBR?.toLocaleString(),
          data[endDate]?.count?.toLocaleString(),
          data[endDate]?.sum_DEPSUMBR?.toLocaleString(),
          data["change"]?.count?.toLocaleString(),
          data["change"]?.sum_DEPSUMBR?.toLocaleString(),
          data?.change?.rate
            ? `${(data.change.rate * 100)
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%`
            : "-",
        ];
      });
      setPrevData(growthRateTableData);
      const downloadTotals = [
        "National Totals",
        "",
        "",
        "",
        totals?.startDate?.count?.toLocaleString(),
        totals?.startDate?.sum_DEPSUMBR?.toLocaleString(),
        totals?.endDate?.count?.toLocaleString(),
        totals?.endDate?.sum_DEPSUMBR?.toLocaleString(),

        totals?.change?.count?.toLocaleString(),
        totals?.change?.sum_DEPSUMBR?.toLocaleString(),
        totals?.rate
          ? `${(totals.rate * 100)
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%`
          : "-",
      ];
      setDownloadData([downloadHeaders, ...newDataArray, downloadTotals]);
    }
  }, [
    appState.filters.bhcRSSD,
    appState.filters.cert,
    bankName,
    downloadHeaders,
    endDate,
    growthRateTableData,
    prevData,
    startDate,
    totals,
  ]);

  const tableBody: React.ReactElement[] =
    recordCount > 0 && paginatedData
      ? paginatedData.map((data: KeyValue, int: number) => (
          <TableRow id={data.MSABR} key={`${data.MSABR}_${int}`}>
            <TableCell
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
              style={{
                textAlign: "left",
              }}
            >
              {data.MSANAMB}
            </TableCell>
            <TableCell
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
            ></TableCell>
            <TableCell
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
            >
              {data.MSABR}
            </TableCell>
            <TableCell
              {...tableCellProps(
                data.MSANAMB,
                startDate,
                int,
                data[startDate]?.count?.toLocaleString(),
              )}
              style={
                data[startDate]?.count
                  ? {
                      color: "#1265ff",
                      textDecoration: "underline",
                    }
                  : {}
              }
            >
              {data[startDate]?.count?.toLocaleString()}
            </TableCell>
            <TableCell
              {...tableCellProps(
                data.MSANAMB,
                startDate,
                int,
                data[startDate]?.count?.toLocaleString(),
              )}
              style={
                data[startDate]?.count
                  ? {
                      color: "#1265ff",
                      textDecoration: "underline",
                      textAlign: "right",
                    }
                  : { textAlign: "right" }
              }
            >
              {data[startDate]?.sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell
              {...tableCellProps(
                data.MSANAMB,
                endDate,
                int,
                data[endDate]?.count?.toLocaleString(),
                true,
              )}
              style={
                data[endDate]?.count
                  ? {
                      color: "#1265ff",
                      textDecoration: "underline",
                    }
                  : {}
              }
            >
              {data[endDate]?.count?.toLocaleString()}
            </TableCell>
            <TableCell
              {...tableCellProps(
                data.MSANAMB,
                endDate,
                int,
                data[endDate]?.count?.toLocaleString(),
                true,
              )}
              colSpan={1}
              style={
                data[endDate]?.count
                  ? {
                      color: "#1265ff",
                      textDecoration: "underline",
                      textAlign: "right",
                    }
                  : { textAlign: "right" }
              }
            >
              {data[endDate]?.sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
            >
              {data.change?.count?.toLocaleString()}
            </TableCell>
            <TableCell
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
              style={{
                textAlign: "right",
              }}
            >
              {data.change?.sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
            >
              {`${(data.change?.rate * 100).toFixed(2)}%`}
            </TableCell>
          </TableRow>
        ))
      : [
          <tr key="no-results">
            <td colSpan={7}>
              Double check your search criteria for errors and try your search
              again.
            </td>
          </tr>,
        ];

  const tableTotals: JSX.Element | JSX.Element[] =
    recordCount > 0 && paginatedData ? (
      <TableRow id="totals" key="totals">
        <TableCell style={headerStyle(primaryHeaderColor, "left")}>
          National Totals
        </TableCell>
        <TableCell style={headerStyle(primaryHeaderColor, "")}></TableCell>
        <TableCell style={headerStyle(primaryHeaderColor, "")}></TableCell>
        <TableCell style={headerStyle(secondaryHeaderColor, "center")}>
          {totals?.startDate?.count?.toLocaleString() || 0}
        </TableCell>
        <TableCell style={headerStyle(secondaryHeaderColor, "right")}>
          {totals?.startDate?.sum_DEPSUMBR?.toLocaleString() || 0}
        </TableCell>
        <TableCell style={headerStyle(additionalHeaderColor, "center")}>
          {totals?.endDate?.count?.toLocaleString() || 0}
        </TableCell>
        <TableCell style={headerStyle(additionalHeaderColor, "right")}>
          {totals?.endDate?.sum_DEPSUMBR?.toLocaleString() || 0}
        </TableCell>
        <TableCell style={headerStyle(secondaryHeaderColor, "center")}>
          {totals?.change?.count?.toLocaleString() || 0}
        </TableCell>
        <TableCell style={headerStyle(secondaryHeaderColor, "right")}>
          {totals?.change?.sum_DEPSUMBR?.toLocaleString() || 0}
        </TableCell>
        <TableCell style={headerStyle(primaryHeaderColor, "center")}>
          {(totals?.rate * 100).toFixed(2)}%
        </TableCell>
      </TableRow>
    ) : (
      [<tr key="no-results"></tr>]
    );

  const importantInfoLink = appState.filters.bhcRSSD ? (
    <Button
      className={classes.buttonLink}
      onClick={() => window.scrollTo(0, document.body.scrollHeight)}
    >
      Click for important information about this data.
    </Button>
  ) : (
    <></>
  );
  return (
    <Root>
      <Typography className={classes.heading}>
        {!bankName
          ? "No Results"
          : `${
              appState.filters.bhcRSSD
                ? "Combined Insured Subsidiaries of "
                : ""
            }${bankName}`}
      </Typography>
      <ApiDataDownload
        app="SOD"
        apiData={[
          {
            appState,
            fileName: `${fileNameFormat("Growth Rate Results MSA")}.csv`,
            apiPath: "sod",
            text: "Download Results Data - formatted",
          },
        ]}
        downloadData={downloadData}
      />

      <Divider />
      {searchPills}
      {importantInfoLink}
      <ResultPagination count={recordCount}>
        {branchOfficeModal}
        <MultiHeaderTable
          tableBody={
            loading ? (
              <LoadingIndicator text="Loading" spinnerSize={32} table />
            ) : (
              <>
                {tableBody}
                {tableTotals}
              </>
            )
          }
          tableHeaders={tableHeaders}
        />
      </ResultPagination>
    </Root>
  );
}
